import { TRACK_CART_SOURCE } from '~/constants/rudderstack';

const GTM_EVENT_NAME = {
  PRODUCT_ADDED: 'EEcartAdd',
  PRODUCT_REMOVED: 'EEcartRemove',
  PRODUCT_VIEWED: 'EEproductView',
};

/**
 * Transform the product object from the Shopify app to the format expected by the GTM data layer
 *
 * @param {Object} product - The product object
 * @param {string} sizeSelected - The size of the variant
 * @returns {Object} - The transformed product object
 */
const _transformProductForGtm = (product, sizeSelected) => {
  let sku = Object.values(product?.sizes)[0]?.sku;
  let size = Object.values(product?.sizes)[0]?.title;
  if (sizeSelected) {
    size = sizeSelected;
    sku = Object.values(product?.sizes).find((s) => s.title === sizeSelected)?.sku || sku;
  }
  const price = (product?.price / 100).toFixed(0);

  return {
    category: product?.type,
    dimension1: size,
    dimension18: product?.id,
    dimension2: decodeURIComponent(product?.colorName),
    dimension21: product?.priceTier,
    dimension22: price,
    id: sku,
    name: decodeURIComponent(product?.masterName),
    price,
    variant: `${size} / ${decodeURIComponent(product?.colorName)}`,
  };
};

/**
 * Transform the product object from the Vue app to the format expected by the GTM data layer
 *
 * @param {Object} product - The product object
 * @returns {Object} - The transformed product object
 */
const _transformAjaxProductForGtm = (product) => {
  const price = `${(product?.price / 100).toFixed(0)}`;
  const sku = product?.sku;
  const size = product?.variant_title;
  const colorName = product?.properties?._color_name;
  return {
    category: product.product_type,
    dimension1: size,
    dimension18: product.id,
    dimension2: product.properties?._color_name,
    dimension21: product.priceTier || 'msrp',
    dimension22: price,
    id: sku,
    name: product.properties?._master_name,
    price,
    variant: `${size} / ${colorName}`,
  };
};

/**
 * Returns the correct transform function based on the source
 *
 * @param {string} source - The source of the event
 * @returns {Function} - The transformed product object
 */
const _transformProduct = (source) =>
  source === TRACK_CART_SOURCE.CART ? _transformAjaxProductForGtm : _transformProductForGtm;

/**
 * Send the product viewed event to GTM
 *
 * @param {Object} product - The product object
 * @param {string} sizeSelected - The size of the variant
 */
const gtmProductViewed = (product, sizeSelected) => {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    ecommerce: {
      detail: {
        products: [_transformProductForGtm(product, sizeSelected)],
      },
    },
    event: GTM_EVENT_NAME.PRODUCT_VIEWED,
  });
};

/**
 * Send the product removed event to GTM
 *
 * @param {Object} productRemoved - The product object
 * @param {Object} cart - The cart object
 */
const gtmProductRemoved = (productRemoved, cart) => {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    ecommerce: {
      cartLineItems: cart?.items?.map(_transformAjaxProductForGtm),
      currencyCode: cart?.currency,
      remove: {
        products: [_transformAjaxProductForGtm(productRemoved)],
      },
    },
    event: GTM_EVENT_NAME.PRODUCT_REMOVED,
  });
};

/**
 * Send the product added event to GTM
 *
 * @param {Object} productToUpdate - The product object
 * @param {Object} cart - The cart object
 * @param {string} source - The source of the event
 */
const gtmProductAdded = (productToUpdate, cart, source) => {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    ecommerce: {
      add: {
        products: [_transformProduct(source)(productToUpdate)],
      },
      cartLineItems: cart?.items?.map(_transformAjaxProductForGtm),
      currencyCode: cart?.currency,
    },
    event: GTM_EVENT_NAME.PRODUCT_ADDED,
  });
};

export { gtmProductAdded, gtmProductRemoved, gtmProductViewed };
